import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Card,
    CardHeader,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    TextField
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import { PATHS } from '../../constants'

// Transfer list helper functions
function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1)
}

function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1)
}

// Available store settings that can be copied
const AVAILABLE_SETTINGS = [
    { id: 'hlg_adjustments', label: 'HLG調整', description: '時間帯別の人員調整設定' },
    { id: 'store_hours', label: '営業時間', description: '曜日別の営業時間設定' },
    { id: 'revenue_staffings', label: '売上人員テーブル', description: '売上に応じた必要人員設定' },
    { id: 'daily_note_templates', label: 'デイリーノートテンプレート', description: '日次メモのテンプレート' }
]

const TransferList = ({ left, right, setLeft, setRight }) => {
    const [checked, setChecked] = useState([])

    const leftChecked = intersection(checked, left)
    const rightChecked = intersection(checked, right)

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value)
        const newChecked = [...checked]

        if (currentIndex === -1) {
            newChecked.push(value)
        } else {
            newChecked.splice(currentIndex, 1)
        }

        setChecked(newChecked)
    }

    const handleAllRight = () => {
        setRight(right.concat(left))
        setLeft([])
    }

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked))
        setLeft(not(left, leftChecked))
        setChecked(not(checked, leftChecked))
    }

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked))
        setRight(not(right, rightChecked))
        setChecked(not(checked, rightChecked))
    }

    const handleAllLeft = () => {
        setLeft(left.concat(right))
        setRight([])
    }

    const customList = (title, items) => (
        <Card sx={{ width: 250 }}>
            <CardHeader
                sx={{ px: 2, py: 1, bgcolor: '#f5f5f5' }}
                title={<Typography variant="subtitle2">{title}</Typography>}
                subheader={<Typography variant="caption">{items.length}件選択中</Typography>}
            />
            <Divider />
            <List
                sx={{
                    height: 280,
                    overflow: 'auto',
                    bgcolor: 'background.paper'
                }}
                dense
            >
                {items.map((item) => {
                    const setting = AVAILABLE_SETTINGS.find(s => s.id === item)
                    return (
                        <ListItem
                            key={item}
                            role="listitem"
                            button
                            onClick={handleToggle(item)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.indexOf(item) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={setting?.label}
                                secondary={setting?.description}
                            />
                        </ListItem>
                    )
                })}
            </List>
        </Card>
    )

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            {customList('コピー可能な設定', left)}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAllRight}
                    disabled={left.length === 0}
                >
                    ≫
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCheckedRight}
                    disabled={leftChecked.length === 0}
                >
                    <ChevronRightIcon />
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCheckedLeft}
                    disabled={rightChecked.length === 0}
                >
                    <ChevronLeftIcon />
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAllLeft}
                    disabled={right.length === 0}
                >
                    ≪
                </Button>
            </Box>
            {customList('コピーする設定', right)}
        </Box>
    )
}

// Day name mappings for display
const DAY_NAMES = {
    monday: '月曜日',
    tuesday: '火曜日',
    wednesday: '水曜日',
    thursday: '木曜日',
    friday: '金曜日',
    saturday: '土曜日',
    sunday: '日曜日',
    irregular: '特別'
}

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'irregular']

const StoreRegistrationPage = ({ history }) => {
    const [sourceStore, setSourceStore] = useState(null)
    const [targetCompany, setTargetCompany] = useState(null)
    const [storeType, setStoreType] = useState(null)
    const [storeHours, setStoreHours] = useState([])
    const [employees, setEmployees] = useState([])
    const [loading, setLoading] = useState(true)
    const [settingsModalOpen, setSettingsModalOpen] = useState(false)
    const [leftSettings, setLeftSettings] = useState(AVAILABLE_SETTINGS.map(s => s.id))
    const [rightSettings, setRightSettings] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    
    // New store attributes
    const [newStoreName, setNewStoreName] = useState(history.location.state.store.name)
    const [newStoreSegCode, setNewStoreSegCode] = useState(history.location.state.store.seg_code)

    useEffect(() => {
        const state = history?.location?.state
        if (!state?.store || !state?.company) {
            history.push(PATHS.adminStoreManagements)
            return
        }

        setSourceStore(state.store)
        setTargetCompany(state.company)
        
        // Pre-fill new store name with source store name
        setNewStoreName(state.store.name || '')

        // Fetch additional store details including store hours
        Promise.all([
            AxiosWithAuth.get('/store_types'),
            AxiosWithAuth.get(`/employments/store/${state.store.seg_code}`),
            AxiosWithAuth.get(`/stores/${state.store.seg_code}`)
        ])
            .then(([storeTypesRes, employmentsRes, storeDetailsRes]) => {
                const storeTypes = storeTypesRes.data
                const type = storeTypes.find(t => t.id === state.store.store_type_id)
                setStoreType(type)
                setEmployees(employmentsRes.data || [])
                
                // Sort store hours by day order
                const hours = storeDetailsRes.data?.store_hours || []
                const sortedHours = hours
                    .filter(h => h.day !== 'irregular')
                    .sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day))
                setStoreHours(sortedHours)
                
                setLoading(false)
            })
            .catch(err => {
                console.error(err)
                setLoading(false)
            })
    }, [history])

    const handleOpenSettingsModal = () => {
        // Validate new store fields before opening modal
        if (!newStoreName.trim()) {
            setError('新しい店舗名を入力してください')
            return
        }
        if (!newStoreSegCode.trim()) {
            setError('新しい店舗コードを入力してください')
            return
        }
        setError('')
        setSettingsModalOpen(true)
    }

    const handleCloseSettingsModal = () => {
        setSettingsModalOpen(false)
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        setError('')

        try {
            // Call the new backend endpoint to create store and copy features
            const response = await AxiosWithAuth.post('/stores/copy_with_features', {
                source_store_id: sourceStore.id,
                target_company_id: targetCompany.id,
                store: {
                    seg_code: newStoreSegCode.trim(),
                    name: newStoreName.trim()
                },
                features: rightSettings
            })

            console.log('Store created:', response.data)
            setSuccess(true)
            setSettingsModalOpen(false)

            // Redirect to user bulk upload page after short delay
            setTimeout(() => {
                history.push({
                    pathname: PATHS.adminUserManagements,
                    state: {
                        message: `${newStoreName}を${targetCompany.company_name}に登録しました。${rightSettings.length > 0 ? `\n${rightSettings.length}件の設定をコピーしました。` : ''}`,
                        showBulkUpload: true
                    }
                })
            }, 1500)
        } catch (err) {
            console.error(err)
            const errorMessage = err?.response?.data?.error || err?.response?.data?.errors || '登録に失敗しました。もう一度お試しください。'
            setError(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage)
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push(PATHS.adminStoreManagements)}
                sx={{ mb: 3 }}
            >
                店舗管理に戻る
            </Button>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    登録が完了しました。ユーザー管理ページへ移動します...
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    コピー元店舗情報
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                            店舗名:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {sourceStore?.name}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                            店舗コード:
                        </Typography>
                        <Typography variant="body1">
                            {sourceStore?.seg_code}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                            店舗タイプ:
                        </Typography>
                        <Typography variant="body1">
                            {storeType ? `${storeType.type_name} − ${storeType.brand_name}` : '-'}
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
                    営業時間
                </Typography>

                {storeHours.length > 0 ? (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>曜日</TableCell>
                                <TableCell>開店時間</TableCell>
                                <TableCell>閉店時間</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {storeHours.map((hours, index) => (
                                <TableRow key={index}>
                                    <TableCell>{DAY_NAMES[hours.day] || hours.day}</TableCell>
                                    <TableCell>{hours.open_time}</TableCell>
                                    <TableCell>{hours.close_time}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        営業時間が設定されていません
                    </Typography>
                )}
            </Paper>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    新規店舗情報
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                            登録先会社:
                        </Typography>
                        <Chip label={targetCompany?.company_name} color="primary" variant="outlined" />
                    </Box>

                    <TextField
                        label="新しい店舗名"
                        value={newStoreName}
                        onChange={(e) => setNewStoreName(e.target.value)}
                        fullWidth
                        required
                        placeholder="例: 渋谷店"
                        disabled={submitting || success}
                    />

                    <TextField
                        label="新しい店舗コード"
                        value={newStoreSegCode}
                        onChange={(e) => setNewStoreSegCode(e.target.value)}
                        fullWidth
                        required
                        placeholder="例: 12345"
                        helperText={
                            newStoreSegCode === sourceStore?.seg_code
                                ? `同じ店舗コードを使用する場合、元の店舗のコードは「T_${sourceStore?.seg_code}」に変更されます`
                                : "元の店舗コードをそのまま使用することもできます"
                        }
                        disabled={submitting || success}
                    />
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    この店舗の従業員リスト
                </Typography>

                {employees.length > 0 ? (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>名前</TableCell>
                                <TableCell>社員コード</TableCell>
                                <TableCell>役職</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employees.map((emp) => (
                                <TableRow key={emp.id}>
                                    <TableCell>
                                        {emp.user?.kanji_last} {emp.user?.kanji_first}
                                    </TableCell>
                                    <TableCell>{emp.user?.emp_cd}</TableCell>
                                    <TableCell>
                                        {emp.is_manager ? (
                                            <Chip label="店長" size="small" color="primary" />
                                        ) : (
                                            <Chip label="スタッフ" size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        この店舗に従業員はいません
                    </Typography>
                )}
                
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        <strong>※ ユーザーおよび関連データはコピーされません</strong>
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        従業員、シフト、勤怠データなどのユーザー関連情報は新しい店舗にコピーされません。
                        登録完了後、ユーザーのCSVインポートを行ってください。
                    </Typography>
                </Alert>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleOpenSettingsModal}
                    disabled={submitting || success}
                    sx={{ minWidth: 200 }}
                >
                    登録する
                </Button>
            </Box>

            {/* Settings Transfer Modal */}
            <Dialog
                open={settingsModalOpen}
                onClose={handleCloseSettingsModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
                    コピーする店舗設定を選択
                </DialogTitle>
                <DialogContent sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        元の店舗（{sourceStore?.name}）から新しい店舗（{newStoreName}）にコピーする設定を選択してください。
                    </Typography>
                    <TransferList
                        left={leftSettings}
                        right={rightSettings}
                        setLeft={setLeftSettings}
                        setRight={setRightSettings}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseSettingsModal} color="inherit">
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={16} /> : null}
                    >
                        {submitting ? '登録中...' : '登録する'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default StoreRegistrationPage
